import { supabase } from "@/services/supabase/createClient";
import { useQuery } from "@tanstack/react-query";

/**
 * The reset date is on the same day of the month as the subscription start date,
 * but in the next month. The reset date is always in the future.
 * @param startDate
 */
const getResetDate = (startDate: Date): Date => {
  const today = new Date();

  const startDay = startDate.getDate();
  const todayYear = today.getFullYear();
  const todayMonth = today.getMonth();

  // First, calculate the next potential reset date in the current year/month
  let resetDate = new Date(todayYear, todayMonth, startDay);

  // If the calculated reset date is not in the future, move to the next month
  if (resetDate <= today) {
    // Move to the next month
    resetDate = new Date(todayYear, todayMonth + 1, startDay);
  }

  // If the reset date month overflows to the next year, JS handles it automatically
  return resetDate;
};

const getCreditsConsumption = async (teamId?: string) => {
  const { data, error } = await supabase.rpc("get_credit_consumption", teamId ? { team_uuid: teamId } : undefined);

  if (error) {
    throw new Error("No credit balance or user subscription found");
  }

  const creditQuota = data?.[0].credit_quota || 0;
  const resetDate = getResetDate(new Date(data?.[0].subscription_start_date));
  const subscriptionCreditsLeft = data?.[0].subscription_credits;
  const subscriptionCreditsUsed = Math.max(0, creditQuota - subscriptionCreditsLeft);
  const additionalCredits = Math.max(0, data?.[0].credit_balance - subscriptionCreditsLeft);

  return {
    creditQuota,
    additionalCredits,
    subscriptionCreditsLeft,
    subscriptionCreditsUsed,
    resetDate,
  };
};

export const useCreditConsumption = (teamId?: string) => {
  return useQuery({
    queryKey: ["credit-consumption", teamId],
    queryFn: () => getCreditsConsumption(teamId),
    refetchOnWindowFocus: false,
  });
};
